import { useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Clock, Target, ChevronRight, ArrowRight, Play } from 'lucide-react';
import { useI18n } from './i18n';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const colors = ['#34B2C8', '#F5A623', '#E25A6E'];

function ScenarioVideo({ video }) {
  const videoRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  const startVideo = () => {
    videoRef.current?.play();
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 shadow-lg shadow-slate-900/10">
      <video
        ref={videoRef}
        controls
        preload="metadata"
        poster="/media/secure-trail-poster.jpg"
        playsInline
        onPlay={() => setHasStarted(true)}
        className="block w-full aspect-video bg-slate-950"
        aria-label={video.ariaLabel}
      >
        <source src="/media/secure-trail.mp4" type="video/mp4" />
        {video.fallback}
      </video>

      {!hasStarted && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-slate-950/10">
          <button
            type="button"
            onClick={startVideo}
            className="pointer-events-auto inline-flex items-center gap-3 rounded-full bg-white/95 px-6 py-3 text-sm font-semibold text-slate-900 shadow-xl transition-all duration-200 hover:scale-105 hover:bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#34B2C8]/50"
            aria-label={video.playLabel}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#34B2C8] text-white">
              <Play size={18} fill="currentColor" aria-hidden="true" />
            </span>
            {video.playLabel}
          </button>
        </div>
      )}
    </div>
  );
}

export default function LandingScenarios() {
  const { t } = useI18n();
  const { basePath } = useOutletContext();
  const s = t.scenarios;
  return (
    <section className="pt-32 pb-24 bg-gradient-to-b from-slate-50 to-white min-h-screen">
      <div className="max-w-5xl mx-auto px-6">
        <motion.div initial="hidden" animate="visible">
          <motion.h1
            variants={fadeUp} custom={0}
            className="text-3xl sm:text-4xl font-light text-slate-900 mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {s.title}
          </motion.h1>
          <motion.p variants={fadeUp} custom={1} className="text-base text-slate-500 mb-12 max-w-2xl">
            {s.subtitle}
          </motion.p>
        </motion.div>

        <div className="space-y-8">
          {s.items.map((scenario, i) => (
            <motion.div
              key={i}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={i + 2}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
            >
              <div className="h-1.5" style={{ backgroundColor: colors[i] }} />
              <div className="p-8">
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {scenario.name}
                  </h3>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border" style={{ borderColor: colors[i] + '40', color: colors[i], backgroundColor: colors[i] + '08' }}>
                      <Users size={12} />
                      {scenario.participants}
                    </span>
                    <span className="flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full border border-slate-200 text-slate-500 bg-slate-50">
                      <Clock size={12} />
                      {scenario.duration}
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Target size={14} style={{ color: colors[i] }} />
                      {s.objectives}
                    </h4>
                    <ul className="space-y-2">
                      {scenario.objectives.map((obj, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                          <ChevronRight size={14} className="flex-shrink-0 mt-0.5" style={{ color: colors[i] }} />
                          {obj}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 mb-3">{s.theme}</h4>
                    <p className="text-sm text-slate-500 leading-relaxed">{scenario.theme}</p>
                  </div>
                </div>

                {scenario.video && (
                  <div className="mt-8 pt-8 border-t border-slate-100">
                    <div className="mb-5 max-w-2xl">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#34B2C8] mb-2">
                        {scenario.video.eyebrow}
                      </p>
                      <h4
                        className="text-lg sm:text-xl font-medium text-slate-900 mb-2"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {scenario.video.title}
                      </h4>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {scenario.video.description}
                      </p>
                    </div>

                    <ScenarioVideo video={scenario.video} />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={6}
          className="mt-16 text-center"
        >
          <p className="text-base text-slate-500 mb-6">
            {s.customCta}
          </p>
          <Link
            to={`${basePath}/contact`}
            className="inline-flex items-center gap-2 bg-[#34B2C8] hover:bg-[#2a9ab0] text-white font-medium px-7 py-3 rounded-full transition-all duration-200 hover:scale-[1.03] shadow-lg shadow-[#34B2C8]/20"
          >
            {s.ctaButton}
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
